import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../../../../shared/reducers/modals';
import {Dropdown, LoadingSpinner} from '../../../../shared';
import Table from '../../../../shared/components/Table';
import debug from 'debug';
import {gatherData, getRiskLabel, getUpdatedFilter} from '../../../../shared/utils/utils';
import useQueryObject from '../../../../shared/utils/useQueryObject';
import {getRiskPartner} from '../../../../shared/reducers/user';
import useSWR from 'swr';
import useQueryString from '../../../../shared/utils/useQueryString';
import {RiskBox} from '../../../index';
import {openRiskPartner} from '../../../utils/partners';
import {withTheme} from 'styled-components/macro';

const log = debug('circleblack:advisor:analyze:RiskStatusTable');

const RiskStatusTable = ({container, onRowClick, rowsPerPage, theme}) => {
  const riskStatuses = [
    {
      title: 'Too High',
      image: 'exclamationCircle',
      color: theme.dark ? '#FFCC03' : '#CF790B',
      description: 'Current risk is higher than the set target.',
      displayCondition: h => h.riskStatus === 'Too High',
    },
    {
      title: 'Too Low',
      image: 'exclamationCircle',
      color: '#1A69D2',
      description: 'Current risk is lower than the set target.',
      displayCondition: h => h.riskStatus === 'Too Low',
    },
    {
      title: 'Aligned',
      image: 'checkMarkCircleO',
      color: theme.dark ? '#99FA66' : '#79AF30',
      description: 'Current risk is aligned with the set target.',
      displayCondition: h => h.riskStatus === 'Aligned',
    },
  ];

  const {data: con} = useSWR(container && `/containers/${container.id}`, null, {fallbackData: container});
  log('con', con);
  const [riskStatus] = useQueryString('riskStatus');
  const [filters, setFilters] = useQueryObject(
    'riskStatusTableFilters',
    riskStatus && {riskStatus: {label: riskStatus, value: riskStatus}} || {}
  );
  const gatherRiskStatus = con && gatherData(`/containers/${con.id}/riskalerts`);

  // ETODO Risk Status on Row Click go to Household Overview Change this to be a table of only households

  const dataManager = {
    gatherData: inputValue => gatherRiskStatus({...inputValue, ...getUpdatedFilter(filters)}),
  };

  const riskPartner = getRiskPartner();

  React.useEffect(() => {
    if (dataManager.refresh) {
      dataManager.refresh();
    }
  }, [con, filters]);

  const riskNumberImage = h => <RiskBox action={() => openRiskPartner({
    householdID: (getRiskPartner() !== 'stratifi') ? h.clientID : undefined,
    containerID: (getRiskPartner() === 'stratifi') ? con.id : undefined,
  })}
                                        size={'sm'}
                                        riskPartner={riskPartner}
                                        riskNumber={h.riskNumber}
                                        type={'portfolio'}/>;

  const riskToleranceImage = h => <RiskBox action={() => openRiskPartner({
    householdID: (getRiskPartner() !== 'stratifi') ? h.clientID : undefined,
    containerID: (getRiskPartner() === 'stratifi') ? con.id : undefined,
  })}
                                           size={'sm'}
                                           riskPartner={riskPartner}
                                           riskNumber={h.riskTolerance}
                                           type={'target'}/>;

  const updateFilters = (name, value) => setFilters({...filters, [name]: value});

  log('rendering');

  if (!con) {
    return <LoadingSpinner/>;
  }

  return <Table dataManager={dataManager}
                columns={[
                  {
                    title: 'Household',
                    dataPoint: 'displayName',
                    type: 'text',
                    minWidth: 120,
                    isSortable: true,
                  },
                  ['FIRM', 'BRANCH'].includes(con.type) && {
                    title: 'Name',
                    dataPoint: 'name',
                    type: 'text',
                    minWidth: 80,
                    isSortable: true,
                  },
                  {
                    title: 'Type',
                    dataPoint: 'type',
                    render: row => row.type.toUpperCase(),
                    type: 'text',
                    minWidth: 80,
                    isSortable: true,
                  },
                  {
                    title: 'Assets',
                    dataPoint: 'amount',
                    type: 'currency',
                    minWidth: 80,
                    isSortable: true,
                  },
                  riskPartner && {
                    title: `Actual ${getRiskLabel(riskPartner)}`,
                    dataPoint: 'riskNumber',
                    type: 'custom',
                    render: riskNumberImage,
                    minWidth: 50,
                  },
                  riskPartner && {
                    title: `Target ${getRiskLabel(riskPartner)}`,
                    dataPoint: 'riskTolerance',
                    type: 'custom',
                    render: riskToleranceImage,
                    minWidth: 50,
                  },
                ].filter(c => !!c)}
                defaultSort={{
                  column: 'displayName',
                  desc: false,
                }}
                searches={[
                  {
                    title: 'Household Name',
                    name: 'displayName',
                    isFuzzy: true,
                  },
                ].filter(s => !!s)}
                rowIdentifier={'id'}
                rowOnClick={onRowClick}
                iconsKey={riskStatuses}
                filters={[
                  <Dropdown title={'Risk Status'}
                            key={'Risk Status Filter'}
                            onChange={updateFilters}
                            isSearchable={true}
                            isClearable={true}
                            options={riskStatuses.map(i => ({
                              label: i.title,
                              value: i.title,
                            }))}
                            dataPoint={{
                              name: 'riskStatus',
                              value: filters.riskStatus,
                            }}/>,
                ]}
                rowsPerPage={rowsPerPage}
                filterValues={filters}
                maxHeight={'calc(100vh - 220px)'}/>;
};

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default withTheme(connect(null, mapDispatchToProps)(RiskStatusTable));

RiskStatusTable.propTypes = {
  openModal: PropTypes.func.isRequired,
  container: PropTypes.shape({id: PropTypes.number.isRequired, type: PropTypes.string.isRequired}).isRequired,
  config: PropTypes.shape({models: PropTypes.shape({driftStatuses: PropTypes.object})}).isRequired,
  getConfig: PropTypes.func.isRequired,
  /**
   * Optional on row click action
   */
  onRowClick: PropTypes.func,
  /**
   * Optional rows per page override
   */
  rowsPerPage: PropTypes.number,
  theme: PropTypes.object,
};
